import toast from 'react-hot-toast';

// Toast types
type ToastType = 'success' | 'error' | 'loading' | 'info';

interface ToastOptions {
  message: string;
  type?: ToastType;
  duration?: number;
}

// Default durations
const DURATIONS: Record<ToastType, number> = {
  success: 3000,
  error: 5000,
  loading: Infinity,
  info: 4000,
};

// Show toast with consistent styling
export const showToast = ({ message, type = 'info', duration }: ToastOptions) => {
  const durationMs = duration || DURATIONS[type] || 4000;
  
  switch (type) {
    case 'success':
      toast.success(message, { duration: durationMs });
      break;
    case 'error':
      toast.error(message, { duration: durationMs });
      break;
    case 'loading':
      toast.loading(message, { duration: durationMs });
      break;
    default:
      toast(message, { duration: durationMs });
  }
};

// Promise wrapper with toast handling
export const withToast = <T>(
  promise: Promise<T>,
  options: {
    loading: string;
    success: string;
    error?: string;
  }
): Promise<T> => {
  return toast.promise(promise, {
    loading: options.loading,
    success: options.success,
    error: options.error || 'Something went wrong',
  });
};

// Dismiss all toasts
export const dismissToasts = () => toast.dismiss();

// Custom hook for async operations with toast
export const useAsyncWithToast = () => {
  const execute = async <T>(
    asyncFn: () => Promise<T>,
    options: {
      loading: string;
      success: string;
      error?: string;
    }
  ): Promise<T | null> => {
    try {
      return await withToast(asyncFn(), options);
    } catch {
      return null;
    }
  };

  return { execute };
};

// Error handler that shows toast
export const handleError = (error: unknown, fallbackMessage?: string) => {
  const message = error instanceof Error 
    ? error.message 
    : fallbackMessage || 'An error occurred';
  
  console.error('[Error]', error);
  showToast({ message, type: 'error' });
};
